// Clinic location — map/directions URL + locality (branch) label
//
// Stored under `app_config.extra_json.clinic_location`, mirroring the
// `print_branding` / `clinic_classification` / `main_modules` patterns. It
// rides the existing PowerSync + SyncWrite `extra_json` path, so no schema
// migration or codegen is required.
//
// Both fields exist because WhatsApp templates need them as discrete values:
// the booking confirmation renders a "Get Directions" link and a
// "<clinic> - <locality>" branch line. The clinic's free-text Address cannot
// serve either — a URL is not in it, and pulling a locality out of prose
// breaks as soon as someone reorders the address.
//
// Self-contained (no PowerSync / query-spec imports) so the pure logic can be
// unit-tested in isolation.

/** Clinic location fields editable from Clinic Settings. */
export interface ClinicLocationConfig {
  /** Shareable maps link, e.g. "https://maps.app.goo.gl/…". Empty = unset. */
  mapUrl: string;
  /** Short area/branch label, e.g. "Devanahalli". Empty = unset. */
  locality: string;
}

/** Minimal structural shape of the app_config row this module reads. */
export interface ClinicLocationSource {
  extra_json?: string | null;
}

export const CLINIC_LOCATION_DEFAULTS: ClinicLocationConfig = {
  mapUrl: '',
  locality: '',
};

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function parseExtraJson(extraJson: unknown): Record<string, unknown> {
  if (extraJson == null) return {};
  if (isObject(extraJson)) return extraJson;
  if (typeof extraJson === 'string' && extraJson.trim().length > 0) {
    try {
      const parsed = JSON.parse(extraJson);
      if (isObject(parsed)) return parsed;
    } catch {
      /* fall through */
    }
  }
  return {};
}

/**
 * Normalize a map link. Only http(s) URLs survive — anything else (including a
 * bare "maps.app.goo.gl/…" with no scheme) returns ''. WhatsApp renders the
 * value as literal text, so a malformed link would ship to patients as-is.
 */
export function normalizeMapUrl(value: unknown): string {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (trimmed.length === 0) return '';
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? trimmed : '';
  } catch {
    return '';
  }
}

/**
 * Extract `clinic_location` from an app_config row, applying defaults for any
 * missing or malformed field. Always returns a fully-populated object so the
 * UI and template mapper can consume it without null checks.
 */
export function getClinicLocation(
  row: ClinicLocationSource | null | undefined,
): ClinicLocationConfig {
  const extra = parseExtraJson(row?.extra_json);
  const raw = extra.clinic_location;
  if (!isObject(raw)) return { ...CLINIC_LOCATION_DEFAULTS };
  return {
    mapUrl: normalizeMapUrl(raw.mapUrl),
    locality: typeof raw.locality === 'string' ? raw.locality.trim() : '',
  };
}

/**
 * Serialize `clinic_location` into an updated `extra_json` JSON string,
 * preserving every other top-level key already present.
 */
export function writeClinicLocation(
  currentExtraJson: unknown,
  config: ClinicLocationConfig,
): string {
  const next = { ...parseExtraJson(currentExtraJson) };
  next.clinic_location = {
    mapUrl: normalizeMapUrl(config.mapUrl),
    locality: config.locality.trim(),
  };
  return JSON.stringify(next);
}
